import { Component, Inject, Input, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { MatDrawer } from '@angular/material/sidenav';
import { ActivatedRoute, ChildActivationEnd, NavigationEnd, Router } from '@angular/router';
import { RouteConstants } from '@app/constants/route.constants';
import { AuthenticationService } from '@app/security/authentication.service';
import { marker } from '@biesbjerg/ngx-translate-extract-marker';
import UserDTO from '@data/models/user/user-dto';
import { ENV } from '@app/constants/global.constants';
import { Env } from '@app/types/env';
import { TextEditorWrapperService } from '@modules/feature-modules/text-editor-wrapper/service/text-editor-wrapper.service';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { GlobalMessageService } from '@shared/services/global-message.service';
import { ContentService } from '@data/services/content.service';
import { ViewTypesConstants } from '@app/constants/viewTypes.constants';
import ContentDTO from '@data/models/content/content-dto';
import { replacerFunc } from '@shared/utils/replacer-function';
import { StatesConstants } from '@app/constants/states.constants';

@UntilDestroy()
@Component({
  selector: 'app-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class HeaderComponent implements OnInit {
  @Input() userDetails: UserDTO;
  @ViewChild('menuDrawer') menuDrawer: MatDrawer;
  public searchInPath: string = null;
  public dbTable: string = null;
  public searchForm: FormGroup = new FormGroup({
    query: new FormControl('', Validators.required),
    searchInPath: new FormControl(false)
  });
  public showSearch = false;
  public searching = false;
  public searchResult: { [key: string]: ContentDTO[] } = null;

  public labels = {
    exit: marker('administration.exit'),
    title: marker('administration.title'),
    users: marker('administration.users'),
    viewProfile: marker('administration.viewProfile'),
    secTitle: marker('administration.secTitle'),
    version: marker('administration.version'),
    signOut: marker('administration.signOut'),
    introduction: marker('content.titles.intro'),
    hnt: marker('content.titles.hnt'),
    procedures: marker('content.titles.procedures'),
    codes: marker('content.titles.codes'),
    farmacs: marker('content.titles.farmacs'),
    escales: marker('content.titles.escales'),
    glossary: marker('content.titles.glossary'),
    abreviaciones: marker('content.titles.abreviacions'),
    pathophysiology: marker('content.titles.pathophysiology'),
    ethic: marker('content.titles.ethic'),
    manuals: marker('content.titles.manuals'),
    contact: marker('content.titles.contact'),
    repositori: marker('content.titles.repositori'),
    search: marker('common.search'),
    searchOnlyIn: marker('common.searchOnlyIn'),
    noResults: marker('common.noResults'),
    numResults: marker('common.numResults'),
    login: marker('login.signIn')
  };

  public isSideBarOpen = false;
  public necessaryClicksToShowStorageInfo = 3;
  public readonly USERS_TEMPLATE_PATH = RouteConstants.USERS;
  public readonly CECOS_TEMPLATE_PATH = RouteConstants.CECOS;
  public readonly SVB_TEMPLATE_PATH = RouteConstants.USVB;
  public readonly SVA_TEMPLATE_PATH = RouteConstants.USVA;
  public readonly FARMACS_TEMPLATE_PATH = RouteConstants.FARMACS;
  public readonly ESCALES_TEMPLATE_PATH = RouteConstants.ESCALES;
  public readonly INTRO_TEMPLATE_PATH = RouteConstants.INT;
  public readonly HNT_TEMPLATE_PATH = RouteConstants.HNT;
  public readonly PROCEDURES_TEMPLATE_PATH = RouteConstants.procediments;
  public readonly CODIS_TEMPLATE_PATH = RouteConstants.codisActivacio;
  public readonly GLOSS_TEMPLATE_PATH = RouteConstants.GLOSSARY;
  public readonly PATHOPHYSIOLOGY_TEMPLATE_PATH = RouteConstants.PATHOPHYSIOLOGY;
  public readonly ETHIC_TEMPLATE_PATH = RouteConstants.ETHIC;
  public readonly MANUALS_TEMPLATE_PATH = RouteConstants.MANUALS;
  public readonly ABREVIACIONES_TEMPLATE_PATH = RouteConstants.ABREVIACIONS;
  public readonly CONTACT_TEMPLATE_PATH = RouteConstants.CONTACT;
  public readonly REPOSITORI_TEMPLATE_PATH = RouteConstants.repositori  ;

  public url: string = null;

  constructor(
    @Inject(ENV) private env: Env,
    private router: Router,
    private authenticationService: AuthenticationService,
    private textEditorWrapperService: TextEditorWrapperService,
    private translateService: TranslateService,
    private globalMessageService: GlobalMessageService,
    private contentService: ContentService,
    private route: ActivatedRoute
  ) {}

  ngOnInit(): void {
    this.getSearchInPath(this.router.url);

    this.textEditorWrapperService.fullScreenEditorChange.pipe(untilDestroyed(this)).subscribe((date) => {
      if (this.menuDrawer?.opened) {
        this.menuDrawer.close();
      }
    });

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    this.router.events.pipe(untilDestroyed(this)).subscribe((event: any) => {
      if (event instanceof NavigationEnd || event instanceof ChildActivationEnd) {
        this.getSearchInPath(this.router.url);
      }
    });
  }

  public isRoleAdmin(): boolean {
    return this.authenticationService.getUserRole() === 'ADMINISTRADOR';
  }

  public isSomeoneLogged(): boolean {
    return !!this.authenticationService.getUserId();
  }

  /**
   * Check if the user is an administrator or a responsible
   *
   * @returns boolean
   */
  public isAdministradorOrResponsable(): boolean {
    const userRole = this.authenticationService.getUserRole();
    return userRole === 'ADMINISTRADOR' || userRole === 'RESPONSABLE';
  }

  public goToLogin(): void {
    this.router.navigate([RouteConstants.LOGIN]);
  }

  public closeSession(): void {
    this.authenticationService.logout();
    this.router.navigate([RouteConstants.EMPTY]);
  }

  public getIcon(forButton = 'menu'): string {
    if (forButton === 'menu') {
      return this.menuDrawer?.opened ? 'close' : 'menu';
    } else {
      return this.showSearch ? 'close' : 'search';
    }
  }

  public showSorageQuota(): void {
    if (!this.necessaryClicksToShowStorageInfo && this.contentService?.progressiveDownloadService?.storageStatus?.setted) {
      this.globalMessageService.showSuccess({
        message: this.translateService.instant(marker('common.sotarageQuota'), {
          percentageUsed: this.contentService.progressiveDownloadService.storageStatus.percentageUsed
        }),
        actionText: this.translateService.instant(marker('common.close')),
        duration: 15000
      });
      this.necessaryClicksToShowStorageInfo = 6;
    } else if (this.necessaryClicksToShowStorageInfo === 6) {
      this.necessaryClicksToShowStorageInfo--;
      setTimeout(() => {
        this.necessaryClicksToShowStorageInfo = 6;
      }, 5000);
    } else if (this.necessaryClicksToShowStorageInfo) {
      this.necessaryClicksToShowStorageInfo--;
    } else {
      this.necessaryClicksToShowStorageInfo = 6;
    }
  }

  public getUserName(): string {
    return this.authenticationService.getUserId();
  }

  public toggleSearch(forceClose = false): void {
    if (forceClose) {
      this.showSearch = false;
      return;
    }
    this.showSearch = !this.showSearch;
    if (this.menuDrawer?.opened) {
      this.menuDrawer.toggle();
    }
    if (this.showSearch) {
      this.searchResult = null;
      this.searchForm.get('query').setValue('');
      this.searchForm.get('searchInPath').setValue(this.searchInPath ? true : false);
      setTimeout(() => {
        document.getElementById('search-input').focus();
      });
    }
  }

  public searchAction(): void {
    this.searchResult = null;
    const value = this.searchForm.value;
    if (value.query && value.query.length >= 3) {
      this.searching = true;
      this.contentService.searchInAllContent(value.query, value.searchInPath ? this.dbTable : null).then((result) => {
        this.searchResult = result;
        this.searching = false;
      });
    } else {
      this.globalMessageService.showError({
        message: this.translateService.instant(marker('common.searchValidationLength'), { length: 3 }),
        actionText: this.translateService.instant(marker('common.close'))
      });
    }
  }

  public getSearchGroups(): string[] {
    if (this.searchResult) {
      return Object.keys(this.searchResult).filter((k) => this.searchResult[k].length > 0);
    }
    return [];
  }

  public getFormattedDate(): string {
    const date = new Date(this.authenticationService.getDateLogin());
    const formattedDate = `${date.getDate()}/${date.getMonth() < 9 ? '0' : ''}${date.getMonth() + 1}/${date.getFullYear()}`;
    const formattedTime = `${date.getHours()}:${date.getMinutes() < 10 ? '0' : ''}${date.getMinutes()}`;
    return `${formattedDate} ${formattedTime}`;
  }

  public getAppVersion(): string {
    return `v${this.env.appVersion}`;
  }

  public getViewTitle(view: string): string {
    return this.translateService.instant(`content.titles.${view.toLowerCase()}`);
  }

  public goToViewContent(content: ContentDTO, view: ViewTypesConstants): void {
    if (content.estat !== StatesConstants.ESB) {
      this.toggleSearch(true);
      this.router.navigateByUrl(
        `/${RouteConstants.EMPTY}/${view}/(cev:${RouteConstants.VIEWER}/${view}/${content.id}/${content.versio})`,
        {
          state: {
            relativeTo: JSON.stringify(this.route, replacerFunc)
          }
        }
      );
    } else {
      this.globalMessageService.showWarning({
        message: this.translateService.instant(marker('common.esbContentMessage')),
        actionText: this.translateService.instant(marker('common.close'))
      });
    }
  }

  public isEsbContent(estat: StatesConstants): boolean {
    return estat === StatesConstants.ESB;
  }

  private getSearchInPath(path: string) {
    if (path.indexOf(`/${RouteConstants.CECOS}`) === 0) {
      this.searchInPath = this.getViewTitle(RouteConstants.CECOS);
      this.dbTable = ViewTypesConstants.CECOS;
      this.url = ViewTypesConstants.CECOS;
    } else if (path.indexOf(`/${RouteConstants.USVB}`) === 0) {
      this.searchInPath = this.getViewTitle(RouteConstants.USVB);
      this.dbTable = ViewTypesConstants.SVB;
      this.url = ViewTypesConstants.SVB;
    } else if (path.indexOf(`/${RouteConstants.USVA}`) === 0) {
      this.searchInPath = this.getViewTitle(RouteConstants.USVA);
      this.dbTable = ViewTypesConstants.SVA;
      this.url = ViewTypesConstants.SVA;
    } else if (path.indexOf(`/${RouteConstants.SVB}`) === 0) {
      this.searchInPath = this.getViewTitle(RouteConstants.SVB);
      this.dbTable = ViewTypesConstants.SVB;
      this.url = ViewTypesConstants.SVB;
    } else if (path.indexOf(`/${RouteConstants.SVA}`) === 0) {
      this.searchInPath = this.getViewTitle(RouteConstants.SVA);
      this.dbTable = ViewTypesConstants.SVA;
      this.url = ViewTypesConstants.SVA;
    } else if (path.indexOf(`/${RouteConstants.FARMACS}`) === 0) {
      this.searchInPath = this.getViewTitle(RouteConstants.FARMACS);
      this.dbTable = ViewTypesConstants.farmacs;
      this.url = ViewTypesConstants.farmacs;
    } else if (path.indexOf(`/${RouteConstants.ESCALES}`) === 0) {
      this.searchInPath = this.getViewTitle(RouteConstants.ESCALES);
      this.dbTable = ViewTypesConstants.escales;
      this.url = ViewTypesConstants.escales;
    } else if (path.indexOf(`/${RouteConstants.procediments}`) === 0) {
      this.searchInPath = this.getViewTitle(RouteConstants.procediments);
      this.dbTable = ViewTypesConstants.procediments;
      this.url = ViewTypesConstants.procediments;
    } else if (path.indexOf(`/${RouteConstants.codisActivacio}`) === 0) {
      this.searchInPath = this.getViewTitle(RouteConstants.codisActivacio);
      this.dbTable = ViewTypesConstants.codisActivacio;
      this.url = ViewTypesConstants.codisActivacio;
    } else if (path.indexOf(`/${RouteConstants.GLOSSARY}`) === 0) {
      this.searchInPath = this.getViewTitle(RouteConstants.GLOSSARY);
      this.dbTable = ViewTypesConstants.glossaris;
      this.url = ViewTypesConstants.glossaris;
    } else if (path.indexOf(`/${RouteConstants.CONTACT}`) === 0) {
      this.url = 'CONTACTE';
    } else {
      this.searchInPath = null;
      this.dbTable = null;
      this.url = null;
    }
  }
}
